import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import addressService, { AddressWithId } from '../services/addressService';
import { toast } from 'react-hot-toast';

// Define the context state type
interface AddressContextState {
  // Address data
  addresses: AddressWithId[];
  selectedAddress: AddressWithId | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  selectAddress: (address: AddressWithId) => void;
  addAddress: (address: any) => Promise<AddressWithId | null>;
  deleteAddress: (addressId: string) => Promise<boolean>;
  refreshAddresses: () => Promise<AddressWithId[]>;
}

// Create the context with default values
const AddressContext = createContext<AddressContextState>({
  addresses: [],
  selectedAddress: null,
  isLoading: false,
  error: null,
  
  // Default empty functions
  selectAddress: () => {},
  addAddress: async () => null,
  deleteAddress: async () => false,
  refreshAddresses: async () => [],
});

// Provider props
interface AddressProviderProps {
  children: ReactNode;
}

// Global flag to prevent duplicate fetching across providers/instances
let globalInitialized = false;

// Create the provider component
export const AddressProvider: React.FC<AddressProviderProps> = ({ children }) => {
  // State
  const [addresses, setAddresses] = useState<AddressWithId[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<AddressWithId | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  // Use a ref to track if a fetch is in progress
  const fetchInProgress = useRef(false);

  // Load addresses on mount, but only once per component instance
  useEffect(() => {
    if (!initialized) {
      console.log('[AddressContext] Initializing, fetching addresses');
      setInitialized(true);
      refreshAddresses();
    }
  }, [initialized]);

  // Reset fetch state when window gains focus - handles navigation between pages
  useEffect(() => {
    const handleFocus = () => {
      console.log('[AddressContext] Window gained focus, resetting fetch state');
      fetchInProgress.current = false;
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Select an address
  const selectAddress = useCallback((address: AddressWithId) => {
    // Prevent selection if it's the same address
    if (selectedAddress && address && selectedAddress.id === address.id) {
      console.log('[AddressContext] Skipping selection, already selected:', address.id);
      return;
    }
    
    console.log('[AddressContext] Selecting address:', address.id);
    setSelectedAddress(address);
  }, [selectedAddress]);

  // Add a new address
  const addAddress = useCallback(async (address: any): Promise<AddressWithId | null> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if we might be adding a duplicate address - simple check by comparing streets
      const potentialDuplicate = addresses.find(addr => 
        addr.street && address.street && 
        addr.street.toLowerCase() === address.street.toLowerCase() &&
        addr.city && address.city &&
        addr.city.toLowerCase() === address.city.toLowerCase()
      );
      
      if (potentialDuplicate) {
        console.log('[AddressContext] Found potential duplicate address, using existing one:', potentialDuplicate.id);
        
        // Auto-select the existing address
        setSelectedAddress(potentialDuplicate);
        
        toast.success('Using existing address');
        return potentialDuplicate;
      }
      
      console.log('[AddressContext] Adding new address');
      const newAddress = await addressService.createAddress(address);
      
      if (newAddress) {
        console.log('[AddressContext] Address added successfully:', newAddress.id);
        
        // Update the addresses list
        setAddresses(prev => [...prev, newAddress]);
        
        // Auto-select the new address
        setSelectedAddress(newAddress);
        
        toast.success('Address added successfully');
        return newAddress;
      }
      
      return null;
    } catch (err) {
      console.error('[AddressContext] Error adding address:', err);
      setError('Failed to add address');
      toast.error('Failed to add address');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [addresses]);

  // Delete an address
  const deleteAddress = useCallback(async (addressId: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('[AddressContext] Deleting address:', addressId);
      const success = await addressService.deleteAddress(addressId);
      
      if (success) {
        console.log('[AddressContext] Address deleted successfully');
        
        // Update addresses list
        setAddresses(prev => prev.filter(addr => addr.id !== addressId));
        
        // Clear selected address if it was deleted
        if (selectedAddress && selectedAddress.id === addressId) {
          setSelectedAddress(null);
        }
        
        toast.success('Address deleted successfully');
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('[AddressContext] Error deleting address:', err);
      setError('Failed to delete address');
      toast.error('Failed to delete address');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [selectedAddress]);

  // Refresh all addresses - clean implementation without any debounce logic
  const refreshAddresses = useCallback(async (): Promise<AddressWithId[]> => {
    // If a fetch is already in progress, return the current addresses
    if (fetchInProgress.current) {
      console.log('[AddressContext] Address fetch already in progress, returning current addresses');
      return addresses;
    }
    
    // Simple state management - set loading, perform fetch, handle result
    try {
      fetchInProgress.current = true;
      setIsLoading(true);
      setError(null);
      
      const data = await addressService.getAddresses();
      
      // Only update state if we got valid data
      if (data) {
        console.log(`[AddressContext] Fetched ${data.length} addresses`);
        setAddresses(data);
        
        // If we have addresses but no selected address, auto-select default or first
        if (data.length > 0 && !selectedAddress) {
          const defaultAddress = data.find(addr => addr.isDefault) || data[0];
          console.log('[AddressContext] Auto-selecting address:', defaultAddress.id);
          setSelectedAddress(defaultAddress);
        }
      }
      
      return data || [];
    } catch (err) {
      console.error('[AddressContext] Error fetching addresses:', err);
      setError('Failed to load addresses');
      return [];
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [addresses, selectedAddress]);

  // Create the context value object
  const contextValue: AddressContextState = {
    addresses,
    selectedAddress,
    isLoading,
    error,
    selectAddress,
    addAddress,
    deleteAddress,
    refreshAddresses
  };

  // Render the provider
  return (
    <AddressContext.Provider value={contextValue}>
      {children}
    </AddressContext.Provider>
  );
};

// Custom hook to use the address context
export const useAddressContext = () => useContext(AddressContext);

// Export the context and provider
export default AddressContext; 